import { Grid, Modal, spacing5 } from '@leaftrade/leaftrade-component-library';
import queryString from 'query-string';
import React, { useEffect, useState } from 'react';
import { useInView } from 'react-intersection-observer';
import { useDispatch } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { useStorefrontState } from '../../../../hooks/useReduxState';
import PageLoader from '../../../../shared/components/PageLoader';
import {
  getQuickOrderProducts,
  toggleQuickOrderAddModal,
} from '../../../../store/actions/storefrontActions';
import {
  GRID_HORIZONTAL_OFFSET,
  GRID_ITEM_CSS_CLASS,
  GRID_ITEM_WIDTH,
  GRID_LIST_VIEW_SX,
} from '../../../constants';
import { LoadMoreWrapper } from '../../../routes/Seller/Seller.styles';
import NoResults from '../../NoResults';
import ProductCard from '../../ProductCard';
import ProductCardBatchQueues from '../../ProductCardBatchQueues';
import SellerHeader from '../../SellerHeader';

const QUICK_ORDER_ADD_PANEL_TOP_OFFSET = '56px';

const AddToQuickOrderModal = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { ref, inView } = useInView();
  const { quickOrderData, vendor } = useStorefrontState();
  const { addToQuickOrderModal } = quickOrderData;
  const { hasAdditionalProduct, isLoadingQuickOrderProducts, products } =
    addToQuickOrderModal;
  const { settings, slug } = vendor;
  const { batchQueuesEnabled, isGridView } = settings;

  const [pageNumber, setPageNumber] = useState(0);

  const queryParamsParsed = queryString.parse(location.search);

  const handleClearQueryParams = () => {
    navigate(location.pathname, { replace: true });
    dispatch(
      getQuickOrderProducts({
        slug,
      })
    );
  };

  useEffect(() => {
    if (addToQuickOrderModal.isOpen) {
      dispatch(
        getQuickOrderProducts({
          queryString: queryString.stringify(queryParamsParsed),
          slug,
        })
      );
    }
  }, [addToQuickOrderModal.isOpen]);

  useEffect(() => {
    if (inView && hasAdditionalProduct) {
      queryParamsParsed.page_num = pageNumber + 1;

      dispatch(
        getQuickOrderProducts({
          isLoadingMore: true,
          page: pageNumber + 1,
          slug,
          queryString: queryString.stringify(queryParamsParsed),
        })
      );

      setPageNumber(pageNumber + 1);
    }
  }, [inView]);

  return (
    <Modal
      header={<SellerHeader isQuickOrder />}
      hideClose={addToQuickOrderModal.isClearingQuickOrder}
      isPanel
      onClose={() =>
        dispatch(
          toggleQuickOrderAddModal({
            isOpen: false,
          })
        )
      }
      open={addToQuickOrderModal.isOpen}
      sx={{
        top: QUICK_ORDER_ADD_PANEL_TOP_OFFSET,
        height: `calc(100% - ${QUICK_ORDER_ADD_PANEL_TOP_OFFSET})`,
        '& .lcl-modal__container': {
          height: `calc(100vh - ${QUICK_ORDER_ADD_PANEL_TOP_OFFSET})`,
        },
        '& [data-testid="modal--header--div"]': {
          paddingLeft: '32px',
        },
        '& [data-testid="modal--content--div"]': {
          maxHeight: 'calc(100% - 180px)',
        },
      }}
      testingId='add-item-quick-order-modal--component--div'
      modalWidth='90vw'
    >
      {isLoadingQuickOrderProducts ? (
        <PageLoader message='Loading' />
      ) : (
        <Grid
          cssItemSelector={`.${GRID_ITEM_CSS_CLASS}`}
          maxItemWidth={
            products?.length && isGridView ? GRID_ITEM_WIDTH : '100%'
          }
          maxColumns={products?.length && isGridView ? 4 : 1}
          gap={spacing5}
          horizontalOffset={GRID_HORIZONTAL_OFFSET}
          sx={isGridView ? {} : GRID_LIST_VIEW_SX}
        >
          {products?.length ? (
            products.map((product) =>
              batchQueuesEnabled ? (
                <ProductCardBatchQueues
                  key={product.id}
                  product={product}
                  slug={slug}
                />
              ) : (
                <ProductCard key={product.id} product={product} slug={slug} />
              )
            )
          ) : (
            <NoResults
              onClear={handleClearQueryParams}
              resultsType='products'
            />
          )}
        </Grid>
      )}
      {hasAdditionalProduct && (
        <LoadMoreWrapper ref={ref}>
          <PageLoader message='Fetching more results' />
        </LoadMoreWrapper>
      )}
    </Modal>
  );
};

export default AddToQuickOrderModal;
